import {
  InteractionRequiredAuthError,
  PublicClientApplication,
  type AccountInfo,
  type Configuration,
} from "@azure/msal-browser";
import { logger } from "../logger";
import { clearLastAccount, writeLastAccount } from "./lastAccount";
import { clearIdleLock, idleLocked, writeLastActivity } from "./sessionSync";
import {
  readProviderChoice,
  restoreProviderChoice,
  snapshotProviderChoice,
  writeProviderChoice,
} from "./providerChoice";
import { keepRedirectRefusal } from "./redirectRefusal";
import { isMsalError, signInErrorCode } from "./signInErrors";
import {
  defaultProviderOf,
  loadAuthSetup,
  type ResolvedAuthConfig,
  type ResolvedAuthSetup,
} from "./runtimeConfig";
import type {
  AuthAccount,
  AuthProviderOption,
  ReauthOutcome,
  SignInOptions,
  SignOutOptions,
  TokenProvider,
} from "./types";

/**
 * MSAL-backed token provider (FR-10): the customer's Entra ID in dedicated
 * mode, Skypoint's Entra External ID and workforce tenant in hosted mode. Same code either
 * way — only the configuration differs, which is why the server takes a LIST of
 * issuers and serves the browser the one it signs in with at runtime
 * (`GET /api/auth/config`, Session 9 contract §2.5).
 *
 * Redirect flow, not popup: a popup is blocked often enough that the first
 * sign-in of the day fails for someone every week, and the identity provider's
 * password reset and sign-up pages return through a redirect regardless.
 *
 * Workforce SSO (2026-09-28): the config may list several ways to sign in, each
 * with its own authority, so each is its own `PublicClientApplication`. Exactly
 * one is started at a time: the one this browser chose (`providerChoice.ts`),
 * else the default. It reads the redirect on `/callback`, renews silently and
 * signs out. Choosing another provider on the sign-in page starts that
 * provider's client just before its redirect leaves the page.
 */
function msalConfig(config: ResolvedAuthConfig): Configuration {
  return {
    auth: {
      clientId: config.clientId,
      authority: config.authority,
      // External ID authorities are not *.microsoftonline.com, so MSAL needs
      // every host named before it will trust the discovery document.
      // External ID has two: the tenant subdomain and the tenant-GUID host its
      // issuer uses.
      knownAuthorities: config.knownAuthorities,
      redirectUri: config.redirectUri,
      postLogoutRedirectUri: config.postLogoutRedirectUri,
    },
    cache: {
      // localStorage (round 9, sign-out fix D, owner decision): a new tab or window of this
      // browser is signed in, instead of starting a second sign-in. SEC-14 no longer leans on
      // the tab dying: the idle clock is shared by every tab (`sessionSync.ts`), and every
      // sign-out, the idle one included, clears this cache in every tab. The trade-off the owner
      // accepted: a script injected into the page could read a token from localStorage as it
      // could from sessionStorage, for longer.
      cacheLocation: "localStorage",
    },
  };
}

/**
 * Whether a cached account belongs to the configured identity provider.
 *
 * MSAL keys its account cache by ORIGIN, not by client id, so after the flip
 * (or a rollback) a tab still holds the previous provider's account. That
 * account must never become the active one: its tokens would be refused, or
 * renewed against the wrong authority. It matches only when its `environment`
 * is a configured host and, when the config names a directory, its `tenantId`
 * is that directory. Hosts and GUIDs compare without case.
 */
function accountMatches(account: AccountInfo, config: ResolvedAuthConfig): boolean {
  const environment = account.environment?.toLowerCase() ?? "";
  if (!config.knownAuthorities.includes(environment)) return false;
  if (!config.tenantId) return true;
  return account.tenantId?.toLowerCase() === config.tenantId.toLowerCase();
}

function selectAccount(
  app: PublicClientApplication,
  config: ResolvedAuthConfig,
  redirected: AccountInfo | null,
): void {
  if (redirected) {
    if (accountMatches(redirected, config)) {
      app.setActiveAccount(redirected);
      logger.info("sign-in redirect completed");
      return;
    }
    logger.warn("sign-in redirect returned an account from another identity provider", {
      "skydata.source": config.source,
    });
  }
  const active = app.getActiveAccount();
  if (active && accountMatches(active, config)) return;
  if (active) {
    app.setActiveAccount(null);
    logger.info("cleared an active account from another identity provider", {
      "skydata.source": config.source,
    });
  }
  const match = app.getAllAccounts().find((account) => accountMatches(account, config));
  if (match) app.setActiveAccount(match);
}

/**
 * Remembers who signed in, for the sign-in page's "Continue as ..." (`lastAccount.ts`): the
 * address and the provider, never a token. Without a provider id or an address there is nothing
 * to continue as.
 */
function rememberAccount(account: AccountInfo, providerId: string | undefined): void {
  const address = toAccount(account)?.email;
  if (providerId && address) writeLastAccount({ address, providerId });
}

/**
 * Where a silent sign-in leaves for (round 10, C3): the redirect URI, as a redirect sign-in
 * returns, so the next document reads the cache and `/callback` finishes as it always does. An
 * object so a test can spy on the navigation jsdom does not implement.
 */
export const silentSignInExit = {
  leave(url: string): void {
    window.location.replace(url);
  },
};

let appPromise: Promise<PublicClientApplication> | null = null;
let activeConfig: ResolvedAuthConfig | null = null;
let activeSetup: ResolvedAuthSetup | null = null;

/** The remembered provider when the server still lists it, else the default. */
function chosenProvider(setup: ResolvedAuthSetup): ResolvedAuthConfig {
  const choice = readProviderChoice();
  return setup.providers.find((provider) => provider.id === choice) ?? defaultProviderOf(setup);
}

async function startClient(config: ResolvedAuthConfig) {
  const app = new PublicClientApplication(msalConfig(config));
  await app.initialize();
  try {
    const result = await app.handleRedirectPromise();
    return { app, result, refusal: null };
  } catch (cause) {
    // Only the identity provider's answer is kept; anything else still fails the start.
    if (!isMsalError(cause)) throw cause;
    return { app, result: null, refusal: cause as Error };
  }
}

async function startMsal(): Promise<PublicClientApplication> {
  const setup = await loadAuthSetup();
  const config = chosenProvider(setup);
  const { app, result, refusal } = await startClient(config);
  activeSetup = setup;
  activeConfig = config;
  if (result?.account) {
    // An interactive sign-in just completed: the one click that lifts the idle lock, and
    // activity, so the shared clock does not end the new session at once (round 9 review).
    clearIdleLock();
    writeLastActivity();
  } else if (idleLocked()) {
    // Locked and no interactive sign-in: a reload, a new or restored tab. Nothing in the cache
    // may sign this document in, so it is cleared and no account is selected. A refusal the
    // redirect brought back is still said (#2006): nobody is signed in.
    await app.clearCache();
    if (refusal) keepRedirectRefusal(refusal);
    return app;
  }
  selectAccount(app, config, result?.account ?? null);
  const active = app.getActiveAccount();
  if (refusal) {
    // A refused step-up or re-authentication of an account still signed in: the start fails as it
    // always did, and Retry signs back in from the cache. Nobody signed in: the page offers every
    // way in beside the refusal's sentence.
    if (active) throw refusal;
    keepRedirectRefusal(refusal);
  }
  if (result?.account && active?.homeAccountId === result.account.homeAccountId) {
    rememberAccount(active, config.id);
  }
  return app;
}

/**
 * The single MSAL instance. `handleRedirectPromise` runs exactly once here,
 * before anything else reads accounts: calling it from more than one place
 * loses the authorization code and bounces the user back to sign-in forever.
 *
 * The configuration is fetched first, inside the same promise, so nothing can
 * reach MSAL before it knows which identity provider it serves. A failure is
 * NOT cached: the promise is dropped, and the next call (the Retry on the
 * "Sign-in is unavailable" page) fetches again.
 */
export function getMsalApp(): Promise<PublicClientApplication> {
  if (!appPromise) {
    appPromise = startMsal().catch((cause: unknown) => {
      appPromise = null;
      throw cause;
    });
  }
  return appPromise;
}

/**
 * The scopes and redirect the provider asks for: those `getMsalApp` resolved,
 * or none when a provider is built without it (tests). There are no build-time
 * sign-in values.
 */
function currentConfig(): Pick<ResolvedAuthConfig, "scopes" | "redirectUri"> &
  Partial<ResolvedAuthConfig> {
  return activeConfig ?? { scopes: [], redirectUri: "" };
}

function toOption(config: ResolvedAuthConfig): AuthProviderOption {
  return {
    id: config.id,
    label: config.label,
    idp: config.idp,
    ...(config.domainHint ? { domainHint: config.domainHint } : {}),
    isDefault: config.isDefault,
  };
}

function toAccount(account: AccountInfo | null): AuthAccount | null {
  if (!account) return null;
  const claims = account.idTokenClaims as
    { email?: string; preferred_username?: string; name?: string } | undefined;
  const email = claims?.email ?? claims?.preferred_username ?? account.username ?? "";
  const rawName = account.name ?? claims?.name ?? "";
  // An identity provider may name an account "unknown" when it has no name
  // claim; show the email local part rather than the word "unknown".
  const name = rawName && rawName.toLowerCase() !== "unknown" ? rawName : email.split("@")[0];
  return { email, name: name || email };
}

export class MsalTokenProvider implements TokenProvider {
  readonly kind = "msal" as const;

  private readonly scopes: string[];
  private readonly redirectUri: string;
  private readonly config: Pick<ResolvedAuthConfig, "scopes" | "redirectUri"> &
    Partial<ResolvedAuthConfig>;
  private readonly setup: ResolvedAuthSetup | null;

  constructor(
    private readonly app: PublicClientApplication,
    config: Pick<ResolvedAuthConfig, "scopes" | "redirectUri"> &
      Partial<ResolvedAuthConfig> = currentConfig(),
  ) {
    this.scopes = config.scopes;
    this.redirectUri = config.redirectUri;
    this.config = config;
    this.setup = activeSetup;
  }

  get account(): AuthAccount | null {
    return toAccount(this.app.getActiveAccount());
  }

  /** Every way to sign in, for the sign-in page; one entry in the single form. */
  get providers(): AuthProviderOption[] {
    return this.setup?.providers.map(toOption) ?? [];
  }

  get supportUrl(): string | undefined {
    return this.setup?.supportUrl;
  }

  /** The SEC-14 automatic logoff the server serves (round 9, fix F); undefined without a setup. */
  get idleTimeoutMinutes(): number | undefined {
    return this.setup?.idleTimeoutMinutes;
  }

  /** The provider this tab booted (`chosenProvider`); "Sign in again" restarts it. */
  get activeProviderId(): string | undefined {
    return this.config.id;
  }

  /**
   * The client and config a sign-in with `providerId` uses: this one when it is the provider
   * already started, else a new client for that provider. An id the config does not list is
   * refused before anything is remembered.
   */
  private async clientFor(providerId: string | undefined) {
    if (!providerId || providerId === this.config.id || !this.setup) {
      return { app: this.app, config: this.config };
    }
    const config = this.setup.providers.find((provider) => provider.id === providerId);
    if (!config) throw new Error(`No sign-in provider "${providerId}" is configured.`);
    const { app } = await startClient(config);
    return { app, config };
  }

  async getAccessToken(): Promise<string | null> {
    // Never a silent token while the idle lock holds (round 9 review): the next session starts
    // with one click on the sign-in page, not from the cache another tab missed clearing.
    if (idleLocked()) return null;
    const account = this.app.getActiveAccount();
    if (!account) return null;
    try {
      const result = await this.app.acquireTokenSilent({ scopes: this.scopes, account });
      return result.accessToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        // The session is genuinely gone: hand back null and let the shell drop
        // to the sign-in screen rather than starting a redirect from the data
        // layer, which would abandon whatever the user was doing.
        logger.info("silent token acquisition needs interaction");
        return null;
      }
      // The code only (issue #2006): Microsoft's text names the directory and the resource.
      logger.error("silent token acquisition failed", undefined, {
        "skydata.reason": signInErrorCode(error) ?? "unknown",
      });
      return null;
    }
  }

  /**
   * Round 10, C3: a silent sign-in with the remembered address, before any redirect. On success
   * the account is active, the idle lock is lifted and the page leaves for the redirect URI, as
   * a redirect sign-in returns. Any failure (interaction required, third-party cookies blocked,
   * the iframe's timeout, an account from another identity provider) answers false, and the
   * caller redirects with the same hint.
   */
  private async signInSilently(
    app: PublicClientApplication,
    config: Pick<ResolvedAuthConfig, "scopes" | "redirectUri"> & Partial<ResolvedAuthConfig>,
    loginHint: string,
  ): Promise<boolean> {
    try {
      const result = await app.ssoSilent({
        scopes: config.scopes,
        redirectUri: config.redirectUri,
        loginHint,
        ...(config.domainHint ? { domainHint: config.domainHint } : {}),
      });
      const account = result.account;
      const fits =
        account !== null &&
        (!config.knownAuthorities || accountMatches(account, config as ResolvedAuthConfig));
      if (!account || !fits) {
        logger.warn("silent sign-in returned no account for this identity provider", {
          "skydata.source": config.source,
        });
        return false;
      }
      app.setActiveAccount(account);
      clearIdleLock();
      writeLastActivity();
      rememberAccount(account, config.id);
      logger.info("silent sign-in completed");
      silentSignInExit.leave(config.redirectUri || "/");
      return true;
    } catch (cause) {
      // The code only: the message can carry the hint, an address.
      logger.info("silent sign-in needs the identity provider's page", {
        "skydata.reason": signInErrorCode(cause) ?? "unknown",
      });
      return false;
    }
  }

  async signIn(_hint?: string, options?: SignInOptions): Promise<void> {
    const { app, config } = await this.clientFor(options?.providerId);
    // Remembered BEFORE the redirect leaves, so `/callback` starts the client that can read the
    // answer (`providerChoice.ts`). Only when there is a choice to remember.
    const remember = (this.setup?.providers.length ?? 0) > 1 && config.id !== undefined;
    const before = remember ? snapshotProviderChoice() : null;
    if (remember && config.id) writeProviderChoice(config.id);
    try {
      if (
        options?.silentFirst &&
        options.loginHint &&
        (await this.signInSilently(app, config, options.loginHint))
      ) {
        return;
      }
      await app.loginRedirect({
        scopes: config.scopes,
        redirectUri: config.redirectUri,
        // The address typed on the sign-in page: the provider's page opens with it filled in.
        ...(options?.loginHint ? { loginHint: options.loginHint } : {}),
        // A Google IdP federated inside External ID: straight to Google.
        ...(config.domainHint ? { domainHint: config.domainHint } : {}),
        // "Use another account" (round 10, C3): the account picker, with no remembered hint.
        // `prompt=login` is kept for the privileged re-authentication only (`reauthenticate`).
        ...(options?.selectAccount ? { prompt: "select_account" } : {}),
      });
    } catch (cause) {
      if (before) restoreProviderChoice(before);
      throw cause;
    }
  }

  /**
   * FR-14, SEC-4: force a fresh `auth_time` on the CURRENT account.
   *
   * `prompt: "login"` is the whole point — without it the identity provider
   * answers from its own session cookie and hands back a token whose
   * `auth_time` is as old as the one the server just refused, so the save would
   * bounce for ever. `account` is passed so the user is not offered an account
   * picker mid-save.
   *
   * This leaves the page, so it answers "redirecting" rather than "confirmed":
   * the caller has nothing to retry in THIS browsing context and must hand what
   * it is holding to `reauthHandoff` before calling. No active account is a
   * different answer again — nobody is being sent anywhere, so the caller says
   * so instead of waiting for a navigation that will not happen.
   */
  async reauthenticate(): Promise<ReauthOutcome> {
    const account = this.app.getActiveAccount();
    if (!account) return "failed";
    await this.app.acquireTokenRedirect({
      scopes: this.scopes,
      account,
      prompt: "login",
      redirectUri: this.redirectUri,
    });
    return "redirecting";
  }

  /**
   * `local` (an idle expiry, or another tab's sign-out): forget this browser's accounts and tokens
   * and stay on the page; the identity provider's own session stands (round 9, sign-out fix B).
   * Otherwise the person chose Sign out: the identity provider ends its session too, and its
   * logout page brings them back to `/sign-in`. Only that forgets the remembered account
   * (`lastAccount.ts`); a local sign-out keeps it for "Continue as ..." (round 10, C3).
   */
  async signOut(options?: SignOutOptions): Promise<void> {
    const account = this.app.getActiveAccount() ?? undefined;
    if (options?.local) {
      await this.app.clearCache({ account });
      this.app.setActiveAccount(null);
      return;
    }
    clearLastAccount();
    await this.app.logoutRedirect({ account });
  }
}
